import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { noop } from 'lodash';

import { ref as PropTypesRef } from 'utilsTS/prop-types';
import Dropdown from 'components/Dropdown/index.js';

import { omitProps } from 'utilsTS/react';
import { ICON } from 'utilsTS/icon';

import {
    SecondaryNavigationHeadingItem,
    SecondaryNavigationHeadingItemProps,
    SecondaryNavigationItem,
    SecondaryNavigationItemProps,
} from './components';

import { determineOpenItem, isItemSelected } from './utils';

export interface SecondaryNavigationProps extends React.ComponentPropsWithoutRef<'nav'> {
    innerRef?: React.Ref<HTMLElement>;
    /** Defines selected item */
    selectedItem:
        | string
        | number
        | SecondaryNavigationItemProps
        | SecondaryNavigationHeadingItemProps;
    /** Defines array of items */
    items: (SecondaryNavigationItemProps | SecondaryNavigationHeadingItemProps)[];

    /** Defines whether the component should be sticky at the top of the viewport */
    sticky?: boolean;
    /** Defines the offset from the top of the viewport */
    stickyOffset?: string | number;
    /** Defines whether icons should be rendered */
    showIcons?: boolean;
    /** Defines whether expanding a collapsible item should collapse the other ones */
    accordion?: boolean;
    /** Defines Dropdown toggle in responsive variant. When set the component will be rendered in a Dropdown */
    dropdownToggle?: React.ReactNode;
    /** Defines a slot above the navigation */
    featuredCard?: React.ReactNode;
    /** Accepts additional className for the whole list */
    listClassName?: string;
    /** Accepts additional className for each item */
    itemClassName?: string;
    /** Accepts additional className for each link */
    linkClassName?: string;
    /** Is called on every item click */
    onItemClick?: React.MouseEventHandler<HTMLLIElement>;
}

export interface SecondaryNavigationStatics {
    ICON: typeof ICON;
}

export const SecondaryNavigation: React.FC<SecondaryNavigationProps> &
    SecondaryNavigationStatics = ({
    innerRef,
    id,
    selectedItem,
    items,
    sticky,
    stickyOffset = 0,
    showIcons = false,
    accordion = false,
    dropdownToggle,
    featuredCard,
    listClassName,
    itemClassName,
    linkClassName,
    className,
    style,
    onItemClick = noop,
    ...rest
}) => {
    const [openItem, setOpenItem] = useState<number | null>(null);

    useEffect(() => {
        const itemToBeOpened = determineOpenItem(items, selectedItem);

        if (itemToBeOpened) {
            setOpenItem(itemToBeOpened);
        }

        return noop;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleItemOpen = (item: number) => {
        if (accordion) {
            setOpenItem(item);
        }
    };

    const handleItemClose = (item: number) => {
        if (accordion && openItem === item) {
            setOpenItem(null);
        }
    };

    const renderItem = (
        item: SecondaryNavigationItemProps | SecondaryNavigationHeadingItemProps,
        index: number,
        lastItem = false,
    ): React.ReactNode => {
        const handleClick: React.MouseEventHandler<HTMLLIElement> = (event) => {
            if (item.onClick) item.onClick(event);
            onItemClick(event);
        };

        if (item.isSection) {
            const sectionItems = item.items || [];

            return (
                <Fragment key={item.id || `nav-section-${index}`}>
                    {sectionItems.map((_item, _index) => renderItem(_item, _index))}
                    {!lastItem && <span className="g-sidenav-section-divider" />}
                </Fragment>
            );
        }

        if (item.items) {
            const { items: subItems, label, ...itemRest } = item;
            const isSelected = isItemSelected(items, item, selectedItem);
            const isSubItemSelected = subItems.some((subItem) => subItem.selected);
            const isOpen = accordion ? openItem === index : isSelected || isSubItemSelected;

            return (
                <SecondaryNavigationHeadingItem
                    key={item.id || `nav-item-${index}`}
                    label={label}
                    showIcon={showIcons}
                    selected={isSelected}
                    subItemSelected={isSubItemSelected}
                    isOpen={isOpen}
                    className={cx(itemClassName)}
                    onBeforeOpen={() => handleItemOpen(index)}
                    onBeforeClose={() => handleItemClose(index)}
                    {...itemRest}
                >
                    {subItems.map((_item, _index) =>
                        renderItem(_item, _index, items.length === _index + 1),
                    )}
                </SecondaryNavigationHeadingItem>
            );
        }

        return (
            <SecondaryNavigationItem
                key={item.id || `nav-item-${index}`}
                {...item}
                selected={isItemSelected(items, item, selectedItem)}
                showIcon={showIcons}
                className={cx(itemClassName, item.className)}
                linkClassName={cx(linkClassName, item.linkClassName)}
                onClick={handleClick}
            />
        );
    };

    const navigation = (
        <nav
            {...omitProps(rest, SecondaryNavigation)}
            ref={innerRef}
            id={id}
            className={cx(
                'g-sidenav',
                dropdownToggle && 'g-sidenav-dropdown',
                sticky && !dropdownToggle && 'g-sidenav-sticky',
                className,
            )}
            style={{ ...style, top: sticky && !dropdownToggle ? stickyOffset : undefined }}
        >
            {featuredCard}
            <ul role="list" className={listClassName}>
                {items.map((_item, _index) =>
                    renderItem(_item, _index, items.length === _index + 1),
                )}
            </ul>
        </nav>
    );

    if (dropdownToggle) {
        return (
            <Dropdown
                toggle={<span className="g-sidenav-dropdown-toggle">{dropdownToggle}</span>}
                alignment={Dropdown.ALIGNMENT.LEFT}
                maxHeight="100%"
            >
                {navigation}
            </Dropdown>
        );
    }

    return navigation;
};

SecondaryNavigation.ICON = ICON;

SecondaryNavigation.displayName = 'SecondaryNavigation';
SecondaryNavigation.propTypes = {
    innerRef: PropTypesRef(),
    id: PropTypes.string,
    selectedItem: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({ ...SecondaryNavigationItem.propTypes }),
    ]).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({ ...SecondaryNavigationItem.propTypes }).isRequired)
        .isRequired,
    sticky: PropTypes.bool,
    stickyOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    showIcons: PropTypes.bool,
    accordion: PropTypes.bool,
    dropdownToggle: PropTypes.node,
    featuredCard: PropTypes.node,
    listClassName: PropTypes.string,
    itemClassName: PropTypes.string,
    linkClassName: PropTypes.string,
    style: PropTypes.shape({}),
    className: PropTypes.string,
    onItemClick: PropTypes.func,
};
